"use client";

import { useEffect, useRef } from "react";

const commitments = [
    {
        category: "Volume",
        items: [
            "Minimum annual offtake: 500 metric tonnes",
            "Maximum annual offtake: 5,000 metric tonnes per counterparty",
            "Quarterly tranches with ±10% volume flexibility",
            "Spot top-up tranches available subject to inventory",
        ],
    },
    {
        category: "Quality",
        items: [
            "Sb min. 99.50% (standard); 99.85% (high-purity grade)",
            "CoA issued by SGS, Bureau Veritas, or Intertek",
            "Umpire assay procedure available on request",
            "Packaging: 25 kg polypropylene drums or 1 MT big bags",
        ],
    },
    {
        category: "Documentation",
        items: [
            "Commercial Invoice, Packing List, CoA, CoC",
            "Certificate of Origin (Form A or EUR.1)",
            "Export Licence copy (UK/EU/US)",
            "Dangerous Goods Declaration (UN2871)",
        ],
    },
    {
        category: "Delivery",
        items: [
            "Standard lead time: 45–60 days from order confirmation",
            "CIF Rotterdam / Antwerp / Shanghai / Durban",
            "DAP available for EU and UK destinations",
            "Bill of Lading issued within 3 business days of vessel departure",
        ],
    },
];

export default function CommitmentsSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.08 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy"
            style={{ padding: "120px 0" }}
            aria-labelledby="commitments-heading"
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                {/* Header */}
                <div
                    className="px-6 lg:px-12 text-center"
                    style={{ maxWidth: "1280px", margin: "0 auto" }}
                >
                    <div
                        className="flex items-center justify-center gap-3 mb-8 reveal"
                    >
                        <span className="type-subheading">Supply Commitments</span>
                    </div>
                    <h1
                        className="reveal reveal-delay-1"
                        style={{
                            fontFamily: "var(--font-serif)",
                            fontSize: "clamp(2.5rem, 5vw, 4rem)",
                            fontWeight: 400,
                            color: "var(--color-white)",
                            lineHeight: 1.05,
                            letterSpacing: "-0.02em",
                            margin: "0 auto",
                        }}
                    >
                            What every agreement
                            <br />
                            includes as standard.
                    </h1>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                    {commitments?.map((group, i) => (
                        <div
                            key={group?.category}
                            className={`reveal reveal-delay-${i + 1}`}
                            style={{
                                background: "var(--color-navy-mid)",
                                border: "1px solid var(--color-border)",
                                borderRadius: "4px",
                                padding: "36px",
                            }}
                        >
                            <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: "var(--color-white)",
                                    marginBottom: "20px",
                                }}
                            >
                                {group?.category}
                            </p>
                            <ul className="flex flex-col gap-3" role="list">
                                {group?.items?.map((item) => (
                                    <li
                                        key={item}
                                        className="flex items-start gap-3"
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.9rem",
                                            color: "rgba(255,255,255,0.38)",
                                            lineHeight: "1.55",
                                        }}
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#b91c1c"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            style={{ marginTop: "3px", flexShrink: 0 }}
                                            aria-hidden="true"
                                        >
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}